import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Send, AlertCircle } from 'lucide-react';
import { Card, CardTitle, CardDescription } from './Card';
import { Button } from './Button';
import { Skeleton } from './EmptyState';
import { api } from '@/lib/api';
import { date } from '@/lib/format';

interface Comment {
  id: string;
  doctype: string;
  document_id: string;
  body: string;
  created_at: string;
  created_by: string;
}

interface CommentsPanelProps {
  doctype: string;
  documentID: string;
}

export function CommentsPanel({ doctype, documentID }: CommentsPanelProps) {
  const qc = useQueryClient();
  const queryKey = ['comments', doctype, documentID];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn:  () => api<{ items: Comment[] }>(`/comments?doctype=${doctype}&document_id=${documentID}`),
    enabled: !!documentID,
  });
  const items = data?.items ?? [];

  const [body, setBody] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => api('/comments', {
      method: 'POST',
      body: { doctype, document_id: documentID, body },
    }),
    onSuccess: () => {
      setBody('');
      void qc.invalidateQueries({ queryKey });
    },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <Card padded={false}>
      <div className="px-5 py-3 border-b border-hairline">
        <CardTitle>
          <MessageSquare className="size-4 inline mr-1.5 text-accent" /> Comments
        </CardTitle>
        <CardDescription>Discussion attached to this record.</CardDescription>
      </div>

      {err && (
        <div className="px-5 py-2 text-caption text-brand-error inline-flex items-start gap-1.5">
          <AlertCircle className="size-3.5 mt-0.5" /> {err}
        </div>
      )}

      {isLoading ? (
        <div className="p-5"><Skeleton className="h-20 w-full" /></div>
      ) : items.length === 0 ? (
        <div className="px-5 py-6 text-center text-caption text-stone">No comments yet.</div>
      ) : (
        <ul className="divide-y divide-hairline">
          {items.map((c) => (
            <li key={c.id} className="px-5 py-3">
              <div className="flex items-center justify-between text-caption text-stone">
                <span className="font-mono">{c.created_by.slice(-6)}</span>
                <span>{date(c.created_at)}</span>
              </div>
              <div className="mt-1 text-body-sm text-ink whitespace-pre-wrap">{c.body}</div>
            </li>
          ))}
        </ul>
      )}

      <div className="px-5 py-3 border-t border-hairline bg-surface-soft">
        <div className="flex items-start gap-2">
          <textarea
            className="input-base flex-1 min-h-[60px] resize-y"
            placeholder="Write a comment…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <Button
            size="sm"
            onClick={() => add.mutate()}
            loading={add.isPending}
            disabled={!body.trim()}
          >
            <Send className="size-3.5" /> Post
          </Button>
        </div>
      </div>
    </Card>
  );
}
